import type { JSX } from 'react';

type StatusHandler = (info: {
	readonly error: { readonly data?: unknown; readonly status: number };
	readonly params: Record<string, string | undefined>;
}) => JSX.Element | null;

interface GeneralErrorBoundaryViewProps {
	readonly defaultStatusHandler?: StatusHandler;
	readonly error: unknown;
	readonly params?: Record<string, string | undefined>;
	readonly statusHandlers?: Record<number, StatusHandler>;
	readonly unexpectedErrorHandler?: (error: unknown) => JSX.Element | null;
}

function isErrorResponseLike(error: unknown): error is { readonly data?: unknown; readonly status: number } {
	return typeof error === 'object' && error !== null && 'status' in error && typeof error.status === 'number';
}

function getErrorMessage(error: unknown): string {
	if (typeof error === 'string') {
		return error;
	}

	if (error && typeof error === 'object' && 'message' in error && typeof error.message === 'string') {
		return error.message;
	}

	console.error('Unable to get error message for error', error);
	return 'Unknown Error';
}

export function GeneralErrorBoundaryView({
	error,
	defaultStatusHandler = ({ error: statusError }) => (
		<p>
			{statusError.status} {String(statusError.data ?? '')}
		</p>
	),
	params = {},
	statusHandlers,
	unexpectedErrorHandler = (unexpectedError) => <p>{getErrorMessage(unexpectedError)}</p>,
}: GeneralErrorBoundaryViewProps) {
	if (typeof document !== 'undefined') {
		console.error(error);
	}

	return (
		<div className="container flex items-center justify-center p-20 text-h2">
			{isErrorResponseLike(error)
				? (statusHandlers?.[error.status] ?? defaultStatusHandler)({
						error,
						params,
					})
				: unexpectedErrorHandler(error)}
		</div>
	);
}
